/* MI Atelier: native scrolling, accessible controls, editor-safe section lifecycle. */
(() => {
  'use strict';
  document.documentElement.classList.replace('no-js', 'js');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px)');
  const finePointer = window.matchMedia('(pointer: fine)');
  const inEditor = Boolean(window.Shopify && window.Shopify.designMode) || document.body.classList.contains('theme-editor');
  const initialized = new WeakSet();
  let revealObserver;
  let parallaxItems = [];
  let frame = 0;
  let resizeObserver;
  const motionAllowed = () => document.body.dataset.motion === 'true' && !reduced.matches && !inEditor;
  const focusables = root => [...root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,textarea,summary')].filter(el => el.getClientRects().length > 0);
  function closeMenu(menu, restore = true) {
    if (!menu) return;
    menu.open = false;
    document.body.classList.remove('menu-is-open');
    const header = menu.closest('[data-header]');
    if (header) header.classList.remove('menu-active');
    const summary = menu.querySelector(':scope > summary');
    if (summary) { summary.setAttribute('aria-expanded', 'false'); if (restore) summary.focus(); }
  }
  function setHeaderHeight() {
    const header = document.querySelector('[data-header]');
    if (header) document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  }
  function updateScroll() {
    frame = 0;
    const header = document.querySelector('[data-header]');
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 24);
    const back = document.querySelector('.back-top');
    if (back) back.classList.toggle('is-visible', window.scrollY > 700);
    if (!motionAllowed() || !desktop.matches || document.body.dataset.parallax !== 'true') return;
    const height = window.innerHeight;
    parallaxItems.forEach(item => {
      const rect = item.getBoundingClientRect();
      if (rect.bottom < -150 || rect.top > height + 150) return;
      const y = Math.max(-24, Math.min(24, (height / 2 - (rect.top + rect.height / 2)) * 0.045));
      item.style.setProperty('--parallax-y', `${y.toFixed(2)}px`);
    });
  }
  const requestScroll = () => { if (!frame) frame = window.requestAnimationFrame(updateScroll); };
  function setupReveals() {
    if (revealObserver) revealObserver.disconnect();
    document.querySelectorAll('[data-reveal]').forEach(el => el.classList.remove('is-pending'));
    if (!motionAllowed() || !('IntersectionObserver' in window)) return;
    revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.remove('is-pending');
      entry.target.classList.add('is-visible');
      revealObserver.unobserve(entry.target);
    }), { threshold: 0.05, rootMargin: '0px 0px -25px 0px' });
    document.querySelectorAll('[data-reveal]:not(.is-visible)').forEach(el => {
      // Never conceal content above or already inside the current viewport.
      if (el.getBoundingClientRect().top < window.innerHeight - 25) el.classList.add('is-visible');
      else { el.classList.add('is-pending'); revealObserver.observe(el); }
    });
  }
  function setupVideo(scope) {
    scope.querySelectorAll('.hero').forEach(hero => {
      const video = hero.querySelector('video');
      const button = hero.querySelector('[data-video-toggle]');
      if (!video || !button || initialized.has(button)) return;
      initialized.add(button);
      const sync = () => {
        const paused = video.paused;
        button.setAttribute('aria-label', paused ? 'Play background video' : 'Pause background video');
        button.setAttribute('aria-pressed', String(paused));
        const label = button.querySelector('[data-video-label]');
        if (label) label.textContent = paused ? 'Play' : 'Pause';
      };
      if (reduced.matches || inEditor) video.pause();
      video.addEventListener('play', sync); video.addEventListener('pause', sync);
      button.addEventListener('click', () => { if (video.paused) video.play().catch(sync); else video.pause(); });
      sync();
    });
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-mobile-menu]').forEach(menu => {
      if (initialized.has(menu)) return;
      initialized.add(menu);
      menu.addEventListener('toggle', () => {
        document.body.classList.toggle('menu-is-open', menu.open);
        const header = menu.closest('[data-header]');
        if (header) header.classList.toggle('menu-active', menu.open);
        menu.querySelector(':scope > summary').setAttribute('aria-expanded', String(menu.open));
      });
      menu.addEventListener('keydown', e => {
        if (!menu.open) return;
        if (e.key === 'Escape') { e.preventDefault(); closeMenu(menu); }
        if (e.key === 'Tab') {
          const list = focusables(menu), first = list[0], last = list[list.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
      menu.querySelectorAll('a').forEach(link => link.addEventListener('click', () => closeMenu(menu, false)));
    });
    scope.querySelectorAll('[data-project-gallery]').forEach(gallery => {
      if (initialized.has(gallery)) return;
      initialized.add(gallery);
      const filters = gallery.querySelector('[data-project-filters]');
      if (!filters) return;
      filters.hidden = false;
      const cards = [...gallery.querySelectorAll('[data-category]')];
      filters.addEventListener('click', e => {
        const button = e.target.closest('[data-filter]');
        if (!button) return;
        const category = button.dataset.filter;
        filters.querySelectorAll('button').forEach(el => { const active = el === button; el.classList.toggle('is-active', active); el.setAttribute('aria-pressed', String(active)); });
        let count = 0;
        cards.forEach(card => { const visible = category === 'all' || card.dataset.category === category; card.hidden = !visible; if (visible) { count++; card.classList.remove('is-pending'); card.classList.add('is-visible'); } });
        const status = gallery.querySelector('[data-filter-status]');
        if (status) status.textContent = `${count} ${count === 1 ? 'project' : 'projects'} shown`;
      });
    });
    scope.querySelectorAll('[data-load-map]').forEach(button => {
      if (initialized.has(button)) return;
      initialized.add(button);
      button.addEventListener('click', () => {
        const iframe = document.createElement('iframe');
        iframe.title = 'MI Design and Construction office location';
        iframe.src = button.dataset.mapUrl;
        iframe.loading = 'lazy';
        iframe.referrerPolicy = 'no-referrer-when-downgrade';
        iframe.allowFullscreen = true;
        const wrapper = button.closest('.map-wrap');
        wrapper.replaceChildren(iframe);
        iframe.focus();
      }, { once: true });
    });
    scope.querySelectorAll('[data-tilt]').forEach(el => {
      if (initialized.has(el)) return;
      initialized.add(el);
      el.addEventListener('pointermove', e => {
        if (!motionAllowed() || !desktop.matches || !finePointer.matches || document.body.dataset.tilt !== 'true') return;
        const rect = el.getBoundingClientRect();
        el.style.setProperty('--tilt-x', `${((e.clientX - rect.left) / rect.width - 0.5) * 9}deg`);
        el.style.setProperty('--tilt-y', `${-((e.clientY - rect.top) / rect.height - 0.5) * 7}deg`);
      });
      el.addEventListener('pointerleave', () => { el.style.removeProperty('--tilt-x'); el.style.removeProperty('--tilt-y'); });
    });
    scope.querySelectorAll('[data-variant-select]').forEach(select => {
      if (initialized.has(select)) return;
      initialized.add(select);
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        const product = select.closest('[data-product]');
        const price = product.querySelector('[data-product-price]');
        if (price) price.textContent = option.dataset.price;
        const button = product.querySelector('[data-add-product]');
        const available = option.dataset.available === 'true';
        if (button) { button.disabled = !available; button.textContent = available ? button.dataset.availableText : button.dataset.soldoutText; }
        const image = product.querySelector('.product-gallery img');
        if (image && option.dataset.image) { image.removeAttribute('srcset'); image.src = option.dataset.image; }
        const url = new URL(window.location.href); url.searchParams.set('variant', option.value); history.replaceState({}, '', url);
      });
    });
    setupVideo(scope);
    parallaxItems = [...document.querySelectorAll('[data-parallax-media]')];
    setupReveals();
    setHeaderHeight(); requestScroll();
    if ('ResizeObserver' in window) {
      if (resizeObserver) resizeObserver.disconnect();
      const header = document.querySelector('[data-header]');
      if (header) { resizeObserver = new ResizeObserver(setHeaderHeight); resizeObserver.observe(header); }
    }
  }
  window.addEventListener('scroll', requestScroll, { passive: true });
  window.addEventListener('resize', () => { setHeaderHeight(); requestScroll(); }, { passive: true });
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') document.querySelectorAll('.nav-dropdown[open]').forEach(details => { details.open = false; details.querySelector('summary').focus(); });
  });
  document.addEventListener('click', e => { document.querySelectorAll('.desktop-nav .nav-dropdown[open]').forEach(details => { if (!details.contains(e.target)) details.open = false; }); });
  desktop.addEventListener('change', () => {
    if (desktop.matches) document.querySelectorAll('[data-mobile-menu]').forEach(menu => closeMenu(menu, false));
    parallaxItems.forEach(el => el.style.removeProperty('--parallax-y'));
    requestScroll();
  });
  reduced.addEventListener('change', () => {
    parallaxItems.forEach(el => el.style.removeProperty('--parallax-y'));
    if (reduced.matches) document.querySelectorAll('video').forEach(video => video.pause());
    setupReveals(); requestScroll();
  });
  document.addEventListener('shopify:section:load', e => init(e.target));
  document.addEventListener('shopify:section:unload', () => { document.body.classList.remove('menu-is-open'); window.requestAnimationFrame(() => init()); });
  document.addEventListener('shopify:block:select', e => {
    const card = e.target.closest('[data-category]');
    if (card) {
      const gallery = card.closest('[data-project-gallery]');
      const all = gallery && gallery.querySelector('[data-filter="all"]');
      if (all) all.click();
    }
  });
  init();
})();
